if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantPrefetch = null;
      variantPrefetchEpoch = 0;
      variantPrefetchUnsubscriber = undefined;
      variantIntentHandler = undefined;
      variantPurchaseLock = undefined;
      variantBusyValue = undefined;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initVariantPrefetch();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.clearVariantPendingState();
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.variantPrefetchUnsubscriber?.();
        ['pointerover', 'pointerdown', 'focusin'].forEach((type) => {
          this.removeEventListener(type, this.variantIntentHandler);
        });
        this.clearVariantPrefetch();
      }

      initVariantPrefetch() {
        this.variantIntentHandler = this.prefetchVariant.bind(this);
        ['pointerover', 'pointerdown', 'focusin'].forEach((type) => {
          this.addEventListener(type, this.variantIntentHandler, { passive: true });
        });
        this.variantPrefetchUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.clearVariantPrefetch.bind(this)
        );
      }

      clearVariantPrefetch() {
        this.variantPrefetchEpoch += 1;
        clearTimeout(this.variantPrefetch?.timeout);
        this.variantPrefetch?.controller.abort();
        this.variantPrefetch = null;
      }

      getVariantPrefetchUrl(target) {
        const picker = this.variantSelectors;
        if (!picker || picker.querySelector('select') || !picker.contains(target)) return null;
        const option = target.closest('label[for], input[type="radio"]');
        const input = option?.tagName === 'LABEL' ? option.control : option;
        if (!input || input.type !== 'radio' || input.checked || !picker.contains(input)) return null;
        const productUrl = input.dataset.productUrl || this.dataset.url;
        if (productUrl !== this.dataset.url) return null;
        const selected = Array.from(picker.querySelectorAll('fieldset input:checked'));
        if (!selected.some((value) => value.name === input.name)) return null;
        const optionValues = selected.map((value) =>
          value.name === input.name ? input.dataset.optionValueId : value.dataset.optionValueId
        );
        if (optionValues.some((value) => !/^\d+$/.test(value || ''))) return null;
        return this.buildRequestUrlWithParams(productUrl, optionValues);
      }

      prefetchVariant(event) {
        if (!this.isConnected || this.pendingRequestUrl || document.visibilityState === 'hidden') return;
        const requestUrl = this.getVariantPrefetchUrl(event.target);
        if (!requestUrl) return;
        const existing = this.variantPrefetch;
        if (
          existing?.requestUrl === requestUrl &&
          Date.now() - (existing.completedAt ?? existing.startedAt) <= 1500
        ) return;
        this.clearVariantPrefetch();
        const entry = {
          requestUrl,
          controller: new AbortController(),
          epoch: this.variantPrefetchEpoch,
          startedAt: Date.now(),
          completedAt: null,
        };
        this.variantPrefetch = entry;
        entry.timeout = setTimeout(() => entry.controller.abort(), 5000);
        entry.promise = this.fetchVariantResponse(requestUrl, entry.controller.signal)
          .then((responseText) => {
            if (!this.isConnected || entry.epoch !== this.variantPrefetchEpoch) return null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const selectedVariant = html.querySelector('variant-selects [data-selected-variant]');
            if (!selectedVariant) throw new Error('Variant prefetch is incomplete');
            const variant = JSON.parse(selectedVariant.textContent);
            if (this.productForm && variant && !html.getElementById(`ProductSubmitButton-${this.sectionId}`)) {
              throw new Error('Variant prefetch purchase form is incomplete');
            }
            entry.completedAt = Date.now();
            return responseText;
          })
          .catch(() => {
            if (this.variantPrefetch === entry) this.variantPrefetch = null;
            return null;
          })
          .finally(() => clearTimeout(entry.timeout));
      }

      fetchVariantResponse(requestUrl, signal) {
        return fetch(requestUrl, { signal }).then((response) => {
          if (!response.ok) throw new Error(`Variant response failed: ${response.status}`);
          return response.text();
        });
      }

      takeVariantResponse(requestUrl, signal) {
        const fetchFresh = () => this.fetchVariantResponse(requestUrl, signal).then((responseText) => ({
          responseText,
          prefetchEpoch: null,
        }));
        const entry = this.variantPrefetch;
        const canReuse =
          entry?.requestUrl === requestUrl &&
          entry.epoch === this.variantPrefetchEpoch &&
          Date.now() - (entry.completedAt ?? entry.startedAt) <= 1500;
        this.variantPrefetch = null;
        clearTimeout(entry?.timeout);
        if (!canReuse) {
          entry?.controller.abort();
          return fetchFresh();
        }
        const abortPrefetch = () => entry.controller.abort();
        signal.addEventListener('abort', abortPrefetch, { once: true });
        return entry.promise
          .then((responseText) => {
            signal.throwIfAborted();
            if (
              responseText &&
              this.isConnected &&
              entry.epoch === this.variantPrefetchEpoch &&
              Date.now() - entry.completedAt <= 1500
            ) return { responseText, prefetchEpoch: entry.epoch };
            return fetchFresh();
          })
          .finally(() => signal.removeEventListener('abort', abortPrefetch));
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        if (this.variantBusyValue === undefined) this.variantBusyValue = this.getAttribute('aria-busy');
        this.setAttribute('data-variant-pending', '');
        this.setAttribute('aria-busy', 'true');
        const productForm = this.productForm;
        if (productForm && !this.variantPurchaseLock) {
          this.variantPurchaseLock = {
            form: productForm,
            wasInert: productForm.hasAttribute('inert'),
          };
          productForm.setAttribute('inert', '');
        }
        if (productForm && !('inert' in productForm)) productForm.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      clearVariantPendingState() {
        const lock = this.variantPurchaseLock;
        if (lock && !lock.wasInert) lock.form.removeAttribute('inert');
        this.variantPurchaseLock = undefined;
        this.removeAttribute('data-variant-pending');
        if (this.variantBusyValue === null) this.removeAttribute('aria-busy');
        else if (this.variantBusyValue !== undefined) this.setAttribute('aria-busy', this.variantBusyValue);
        this.variantBusyValue = undefined;
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        let timedOut = false;
        const recoveryUrl = new URL(requestUrl, window.location.origin);
        recoveryUrl.searchParams.delete('section_id');
        const isCurrentRequest = () => this.abortController === controller && this.isConnected;
        const timeout = setTimeout(() => {
          if (!isCurrentRequest()) return;
          timedOut = true;
          controller.abort();
        }, 10000);

        const applyResponse = ({ responseText, prefetchEpoch }) => {
            if (!isCurrentRequest()) return;
            if (prefetchEpoch !== null && prefetchEpoch !== this.variantPrefetchEpoch) {
              return this.fetchVariantResponse(requestUrl, controller.signal).then((text) =>
                applyResponse({ responseText: text, prefetchEpoch: null })
              );
            }
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const variantData = html.querySelector('variant-selects [data-selected-variant]');
            if (!variantData) throw new Error('Variant response is incomplete');
            const variant = JSON.parse(variantData.textContent);
            if (this.productForm && variant && !html.getElementById(`ProductSubmitButton-${this.sectionId}`)) {
              throw new Error('Variant purchase form is incomplete');
            }
            this.pendingRequestUrl = null;
            callback(html);
            if (!isCurrentRequest()) return;
            this.clearVariantPendingState();
            document.getElementById(targetId)?.focus({ preventScroll: true });
        };
        this.takeVariantResponse(requestUrl, controller.signal)
          .then(applyResponse)
          .catch((error) => {
            if (!isCurrentRequest() || (error.name === 'AbortError' && !timedOut)) return;
            this.pendingRequestUrl = null;
            console.error(error);
            window.location.assign(recoveryUrl.href);
          })
          .finally(() => clearTimeout(timeout));
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        const current = this.variantSelectors;
        if (!variantSelects || !current) return;

        const currentInputs = Array.from(current.querySelectorAll('input[type="radio"]'));
        const newInputs = Array.from(variantSelects.querySelectorAll('input[type="radio"]'));
        const currentLabels = Array.from(current.querySelectorAll('label[for]'));
        const newLabels = Array.from(variantSelects.querySelectorAll('label[for]'));
        const canUpdateInPlace =
          !current.querySelector('select') &&
          !variantSelects.querySelector('select') &&
          currentInputs.length > 0 &&
          currentInputs.length === newInputs.length &&
          currentLabels.length === newLabels.length &&
          currentLabels.every(
            (label, index) =>
              label.htmlFor === newLabels[index].htmlFor &&
              label.innerHTML === newLabels[index].innerHTML
          ) &&
          currentInputs.every(
            (input, index) =>
              input.id === newInputs[index].id &&
              input.name === newInputs[index].name &&
              input.value === newInputs[index].value
          );

        if (!canUpdateInPlace) {
          HTMLUpdateUtility.viewTransition(current, variantSelects, this.preProcessHtmlCallbacks);
          return;
        }

        currentInputs.forEach((input, index) => {
          const updated = newInputs[index];
          Array.from(input.attributes).forEach(({ name }) => {
            if (!updated.hasAttribute(name)) input.removeAttribute(name);
          });
          Array.from(updated.attributes).forEach(({ name, value }) => {
            if (input.getAttribute(name) !== value) input.setAttribute(name, value);
          });
          input.checked = updated.checked;
        });

        const currentValues = current.querySelectorAll('[data-selected-value]');
        const newValues = variantSelects.querySelectorAll('[data-selected-value]');
        currentValues.forEach((value, index) => {
          if (newValues[index] && value.innerHTML !== newValues[index].innerHTML) {
            value.innerHTML = newValues[index].innerHTML;
          }
        });

        const selectedVariant = current.querySelector('[data-selected-variant]');
        const newSelectedVariant = variantSelects.querySelector('[data-selected-variant]');
        if (selectedVariant && newSelectedVariant) selectedVariant.textContent = newSelectedVariant.textContent;
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              if (destination.innerHTML !== source.innerHTML) destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        const mediaOrderMatches =
          mediaGallerySource &&
          mediaGalleryDestination &&
          Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'))
            .map((item) => item.dataset.mediaId)
            .join(',') ===
            Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'))
              .map((item) => item.dataset.mediaId)
              .join(',');

        if (mediaGallerySource && mediaGalleryDestination && !mediaOrderMatches) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        const mediaGallery = this.querySelector('media-gallery');
        const featuredMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        const activeMedia = mediaGallery?.querySelector('li[data-media-id].is-active');
        if (activeMedia?.dataset.mediaId !== featuredMediaId) {
          mediaGallery?.setActiveMedia?.(featuredMediaId, true, true);
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent && modalContent.innerHTML !== newModalContent.innerHTML) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            if (current.innerHTML !== updated.innerHTML) current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
